import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface LineItem {
  id: string;
  description: string;
  quantity: number;
  rate: number;
}

const COMPANY = {
  name: 'Bigger Than Hoop Dreams Inc.',
  address: '46 Reistwood Dr',
  city: 'Kitchener, ON N2R 0N8',
  country: 'Canada',
  email: 'harry@bthd.app',
  website: 'bthd.app',
  gst: '728686833 RT 0001',
};

const HST_RATE = 0.13;

function peekInvoiceNumber(): string {
  const year = new Date().getFullYear();
  const key = `bthd_invoice_counter_${year}`;
  const next = parseInt(localStorage.getItem(key) || '0') + 1;
  return String(next).padStart(3, '0');
}

function commitInvoiceNumber(num: string): void {
  const year = new Date().getFullYear();
  const key = `bthd_invoice_counter_${year}`;
  const n = parseInt(num, 10);
  if (!isNaN(n)) localStorage.setItem(key, n.toString());
}

function formatCurrency(amount: number, currency: 'CAD' | 'USD'): string {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
}

function formatDate(iso: string): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${y}-${m}-${d}`;
}

function toInputDate(date: Date): string {
  return date.toISOString().split('T')[0];
}

export default function InvoicePage() {
  const navigate = useNavigate();

  const today = new Date();
  const due30 = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);

  const [invoiceNumber, setInvoiceNumber] = useState(peekInvoiceNumber);
  const [invoiceDate, setInvoiceDate] = useState(toInputDate(today));
  const [dueDate, setDueDate] = useState(toInputDate(due30));
  const [currency, setCurrency] = useState<'CAD' | 'USD'>('CAD');
  const [includeHST, setIncludeHST] = useState(true);
  const [phone, setPhone] = useState('416-723-3660');
  const [clientName, setClientName] = useState('');
  const [clientAttn, setClientAttn] = useState('');
  const [clientAddress, setClientAddress] = useState('');
  const [clientCity, setClientCity] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [lineItems, setLineItems] = useState<LineItem[]>([
    { id: '1', description: '', quantity: 1, rate: 0 },
  ]);

  const addLine = () =>
    setLineItems(p => [...p, { id: Date.now().toString(), description: '', quantity: 1, rate: 0 }]);
  const removeLine = (id: string) =>
    setLineItems(p => p.filter(i => i.id !== id));
  const updateLine = (id: string, field: keyof Omit<LineItem, 'id'>, value: string | number) =>
    setLineItems(p => p.map(i => i.id === id ? { ...i, [field]: value } : i));

  const subtotal = lineItems.reduce((sum, i) => sum + i.quantity * i.rate, 0);
  const hst = includeHST ? subtotal * HST_RATE : 0;
  const total = subtotal + hst;

  const ctrl: React.CSSProperties = {
    width: '100%',
    background: 'var(--surface-2)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    padding: '8px 10px',
    color: 'var(--text)',
    fontSize: 12,
    fontFamily: 'Barlow, sans-serif',
    outline: 'none',
  };

  const lineInput: React.CSSProperties = {
    border: 'none',
    outline: 'none',
    background: 'white',
    fontFamily: 'Barlow, sans-serif',
    color: '#111',
    fontSize: 13,
    width: '100%',
    padding: 0,
    appearance: 'none' as any,
    MozAppearance: 'textfield' as any,
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex' }}>

      {/* Spinner removal for number inputs */}
      <style>{`
        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        input[type=number] { -moz-appearance: textfield; }
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          #invoice-preview { box-shadow: none !important; border-radius: 0 !important; width: 100% !important; padding: 40px !important; }
        }
      `}</style>

      {/* ── LEFT: Controls ── */}
      <div className="no-print" style={{
        width: 360, flexShrink: 0,
        background: 'var(--surface)',
        borderRight: '1px solid var(--border)',
        padding: 24, overflowY: 'auto',
        display: 'flex', flexDirection: 'column', gap: 20,
      }}>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 800, fontSize: 18 }}>Invoice Generator</div>
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>#{invoiceNumber}</div>
          </div>
          <button onClick={() => navigate('/admin')} className="btn btn-ghost btn-sm">← Back</button>
        </div>

        {/* Invoice Number */}
        <div>
          <div className="label-caps" style={{ marginBottom: 6 }}>Invoice Number</div>
          <input value={invoiceNumber} onChange={e => setInvoiceNumber(e.target.value)} style={ctrl} />
        </div>

        {/* Currency */}
        <div>
          <div className="label-caps" style={{ marginBottom: 8 }}>Currency</div>
          <div style={{ display: 'flex', gap: 8 }}>
            {(['CAD', 'USD'] as const).map(c => (
              <button key={c} onClick={() => setCurrency(c)} style={{
                flex: 1, padding: 8, borderRadius: 8, border: '1.5px solid',
                borderColor: currency === c ? 'var(--navy)' : 'var(--border)',
                background: currency === c ? 'rgba(1,76,141,0.18)' : 'transparent',
                color: currency === c ? 'var(--text)' : 'var(--text-2)',
                fontWeight: 700, cursor: 'pointer', fontSize: 13, fontFamily: 'Barlow, sans-serif',
              }}>{c}</button>
            ))}
          </div>
        </div>

        {/* Dates */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <div className="label-caps" style={{ marginBottom: 6 }}>Invoice Date</div>
            <input type="date" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)} style={ctrl} />
          </div>
          <div>
            <div className="label-caps" style={{ marginBottom: 6 }}>Due Date</div>
            <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} style={ctrl} />
          </div>
        </div>

        {/* Phone */}
        <div>
          <div className="label-caps" style={{ marginBottom: 6 }}>Phone Number</div>
          <input value={phone} onChange={e => setPhone(e.target.value)} style={ctrl} />
        </div>

        {/* Bill To */}
        <div>
          <div className="label-caps" style={{ marginBottom: 10 }}>Bill To</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <input placeholder="Client / Organization Name" value={clientName} onChange={e => setClientName(e.target.value)} style={ctrl} />
            <input placeholder="Attention / c/o" value={clientAttn} onChange={e => setClientAttn(e.target.value)} style={ctrl} />
            <input placeholder="Street Address" value={clientAddress} onChange={e => setClientAddress(e.target.value)} style={ctrl} />
            <input placeholder="City, Province / Country" value={clientCity} onChange={e => setClientCity(e.target.value)} style={ctrl} />
            <input placeholder="Email (optional)" value={clientEmail} onChange={e => setClientEmail(e.target.value)} style={ctrl} />
          </div>
        </div>

        {/* HST */}
        <label style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 12px', background: 'var(--surface-2)',
          borderRadius: 8, border: '1px solid var(--border)',
          cursor: 'pointer', userSelect: 'none',
        }}>
          <input type="checkbox" checked={includeHST} onChange={e => setIncludeHST(e.target.checked)}
            style={{ width: 15, height: 15, cursor: 'pointer', accentColor: 'var(--navy)' }} />
          <span style={{ fontSize: 13 }}>Apply Ontario HST (13%)</span>
        </label>

        {/* Notes */}
        <div>
          <div className="label-caps" style={{ marginBottom: 8 }}>Notes / Payment Instructions</div>
          <textarea value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="Payment instructions, bank details, e-transfer info, etc."
            rows={5} style={{ ...ctrl, resize: 'vertical' }} />
        </div>

        <button onClick={() => { commitInvoiceNumber(invoiceNumber); window.print(); }} className="btn btn-primary"
          style={{ justifyContent: 'center', fontSize: 14, marginTop: 'auto' }}>
          ↓ Download PDF
        </button>
      </div>

      {/* ── RIGHT: Static Preview ── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 40, display: 'flex', justifyContent: 'center' }}>
        <div id="invoice-preview" style={{
          width: 740,
          background: 'white',
          borderRadius: 12,
          boxShadow: '0 8px 48px rgba(0,0,0,0.35)',
          padding: '52px 56px',
          color: '#111',
          fontFamily: 'Barlow, sans-serif',
        }}>

          {/* Header Row 1: logo + INVOICE title */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <img
              src="/bthd-logo-acronym-print-transparent.png"
              alt="BTHD"
              style={{ height: 72, width: 'auto', display: 'block' }}
            />
            <div style={{
              fontFamily: 'Barlow Condensed, sans-serif',
              fontWeight: 900, fontSize: 40,
              color: '#014c8d', letterSpacing: 2, lineHeight: 1,
            }}>INVOICE</div>
          </div>

          {/* Header Row 2: company info + meta */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32 }}>
            <div style={{ fontSize: 12, color: '#444', lineHeight: 1.85 }}>
              <div style={{ fontWeight: 700, color: '#111', fontSize: 13 }}>{COMPANY.name}</div>
              <div>{COMPANY.address}</div>
              <div>{COMPANY.city}</div>
              <div>{COMPANY.country}</div>
              <div style={{ marginTop: 4 }}>{COMPANY.email}</div>
              <div>{COMPANY.website}</div>
              {phone && <div>{phone}</div>}
              <div style={{ marginTop: 4, color: '#777', fontSize: 11 }}>GST/HST: {COMPANY.gst}</div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, alignItems: 'flex-end' }}>
              {([
                ['Invoice #', invoiceNumber],
                ['Date', formatDate(invoiceDate)],
                ['Due', formatDate(dueDate)],
                ['Currency', currency],
              ] as [string, string][]).map(([label, value]) => (
                <div key={label} style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: '#999' }}>{label}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#111', minWidth: 88, textAlign: 'right' }}>{value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Gradient rule */}
          <div style={{ height: 3, background: 'linear-gradient(90deg, #bf1e2e 0%, #014c8d 100%)', borderRadius: 2, marginBottom: 32 }} />

          {/* Bill To */}
          <div style={{ marginBottom: 32 }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: '#999', marginBottom: 10 }}>Bill To</div>
            <div style={{ fontSize: 13, color: '#111', lineHeight: 1.9 }}>
              {clientName
                ? <div style={{ fontWeight: 700, fontSize: 15 }}>{clientName}</div>
                : <div style={{ fontWeight: 700, fontSize: 15, color: '#ddd' }}>—</div>
              }
              {clientAttn && <div style={{ color: '#555', fontSize: 12 }}>c/o {clientAttn}</div>}
              {clientAddress && <div>{clientAddress}</div>}
              {clientCity && <div>{clientCity}</div>}
              {clientEmail && <div style={{ color: '#666', fontSize: 12 }}>{clientEmail}</div>}
            </div>
          </div>

          {/* Line Items */}
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 8 }}>
            <thead>
              <tr>
                {[
                  { label: 'Description', align: 'left', w: undefined },
                  { label: 'Qty', align: 'right', w: 70 },
                  { label: 'Rate', align: 'right', w: 110 },
                  { label: 'Amount', align: 'right', w: 120 },
                ].map(col => (
                  <th key={col.label} style={{
                    padding: '10px 12px',
                    textAlign: col.align as any,
                    fontSize: 10, fontWeight: 700,
                    letterSpacing: 1.2,
                    textTransform: 'uppercase',
                    color: '#888',
                    background: '#f7f8fa',
                    borderTop: '1px solid #e8eaed',
                    borderBottom: '2px solid #e8eaed',
                    width: col.w,
                  }}>{col.label}</th>
                ))}
                <th className="no-print" style={{ width: 32, background: '#f7f8fa', borderTop: '1px solid #e8eaed', borderBottom: '2px solid #e8eaed' }} />
              </tr>
            </thead>
            <tbody>
              {lineItems.map((item, idx) => (
                <tr key={item.id}>
                  <td style={{ padding: '10px 12px', background: 'white', color: '#111', borderBottom: '1px solid #f0f0f0' }}>
                    <input
                      value={item.description}
                      placeholder="Description"
                      onChange={e => updateLine(item.id, 'description', e.target.value)}
                      style={lineInput}
                    />
                  </td>
                  <td style={{ padding: '10px 12px', background: 'white', color: '#111', borderBottom: '1px solid #f0f0f0', textAlign: 'right' }}>
                    <input
                      type="number" min="1" value={item.quantity}
                      onChange={e => updateLine(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                      style={{ ...lineInput, textAlign: 'right' }}
                    />
                  </td>
                  <td style={{ padding: '10px 12px', background: 'white', color: '#111', borderBottom: '1px solid #f0f0f0', textAlign: 'right' }}>
                    <input
                      type="number" min="0" step="0.01" value={item.rate}
                      onChange={e => updateLine(item.id, 'rate', parseFloat(e.target.value) || 0)}
                      style={{ ...lineInput, textAlign: 'right' }}
                    />
                  </td>
                  <td style={{ padding: '10px 12px', background: 'white', color: '#111', borderBottom: '1px solid #f0f0f0', textAlign: 'right', fontWeight: 600 }}>
                    {formatCurrency(item.quantity * item.rate, currency)}
                  </td>
                  <td className="no-print" style={{ padding: '10px 4px', background: 'white', borderBottom: '1px solid #f0f0f0', textAlign: 'center' }}>
                    {lineItems.length > 1 && (
                      <button onClick={() => removeLine(item.id)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#bf1e2e', fontSize: 18, lineHeight: 1, padding: 0 }}>×</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Add line */}
          <div className="no-print" style={{ marginBottom: 32 }}>
            <button onClick={addLine} style={{
              width: '100%', background: 'none',
              border: '1.5px dashed #e0e0e0', borderRadius: 8,
              padding: '9px 0', color: '#bbb', cursor: 'pointer',
              fontSize: 12, fontFamily: 'Barlow, sans-serif',
            }}>+ Add Line Item</button>
          </div>

          {/* Totals */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 36 }}>
            <div style={{ width: 280 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid #f0f0f0', fontSize: 13, color: '#555' }}>
                <span>Subtotal</span><span>{formatCurrency(subtotal, currency)}</span>
              </div>
              {includeHST && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid #f0f0f0', fontSize: 13, color: '#555' }}>
                  <span>HST (13% Ontario)</span><span>{formatCurrency(hst, currency)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '13px 0', fontSize: 18, fontWeight: 700, color: '#111' }}>
                <span>Total</span>
                <span style={{ color: '#014c8d' }}>{formatCurrency(total, currency)}</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          {notes.trim() && (
            <div style={{ background: '#f9fafb', border: '1px solid #eee', borderRadius: 8, padding: '16px 18px', marginBottom: 36 }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: '#999', marginBottom: 8 }}>
                Notes & Payment Instructions
              </div>
              <div style={{ fontSize: 12, color: '#555', lineHeight: 1.85, whiteSpace: 'pre-line' }}>{notes}</div>
            </div>
          )}

          {/* Footer */}
          <div style={{ borderTop: '1px solid #e8eaed', paddingTop: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 11, color: '#bbb' }}>Bigger Than Hoop Dreams Inc. · bthd.app</div>
            <img
              src="/bthd-logo-acronym-print-transparent.png"
              alt="BTHD"
              style={{ height: 32, width: 'auto' }}
            />
          </div>

        </div>
      </div>
    </div>
  );
}
